import { projectPathLabel } from './project-tree';
/** Case- and accent-insensitive, so "cafe" finds "Café". */
function normalize(value) {
    return value
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .trim();
}
/**
 * How well a project matches a query. Higher is better; -1 means no match.
 *
 * The ranking exists because a plain substring filter puts "Reading group"
 * above "Read" for the query "read", and the thing you typed the whole of
 * should come first. Order: exact name, name starts with, word in the name
 * starts with, name contains, ancestry contains.
 */
export function matchScore(name, path, query) {
    const q = normalize(query);
    if (!q)
        return 0;
    const n = normalize(name);
    if (n === q)
        return 100;
    if (n.startsWith(q))
        return 80;
    if (n.split(/[\s/_-]+/).some((word) => word.startsWith(q)))
        return 60;
    if (n.includes(q))
        return 40;
    // Ancestry last: matching a parent is a weaker signal than matching the
    // thing itself, but "thesis" should still surface everything under Thesis.
    if (normalize(path).includes(q))
        return 20;
    return -1;
}
/**
 * The options a picker should show, filtered, ranked and sorted.
 *
 * `alwaysIncludeId` is load-bearing: without it, opening a task whose project
 * has since been paused shows an empty selection, and saving the form silently
 * moves the task. The current value is always an option even when it fails
 * every filter.
 */
export function searchProjects(projects, { query = '', sort = 'alpha', statuses = ['active'], alwaysIncludeId = null, } = {}) {
    const allowed = new Set(statuses);
    const options = [];
    for (const project of projects) {
        const isPinned = alwaysIncludeId != null && project.id === alwaysIncludeId;
        if (!isPinned && !allowed.has(project.status))
            continue;
        const path = projectPathLabel(projects, project.id);
        const score = matchScore(project.name, path, query);
        // The current selection stays visible even while you type something that
        // doesn't match it — otherwise the field looks empty mid-search.
        if (score < 0 && !isPinned)
            continue;
        options.push({ id: project.id, name: project.name, path, status: project.status, score });
    }
    /**
     * Alphabetical by the label the row actually shows, so a nested project
     * sits under its parent instead of somewhere else entirely. Sorting by the
     * bare name put "Umbrella › Nested child" above "Quark", which reads as
     * unsorted. `id` last so equal labels have a stable order rather than
     * whatever IndexedDB returned.
     */
    const byName = (a, b) => {
        const la = a.path ? `${a.path} › ${a.name}` : a.name;
        const lb = b.path ? `${b.path} › ${b.name}` : b.name;
        return la.localeCompare(lb, undefined, { sensitivity: 'base' }) || a.id.localeCompare(b.id);
    };
    if (query.trim()) {
        // Relevance first while searching — the sort toggle only orders the
        // browsing case, where there is nothing to rank by.
        return options.sort((a, b) => b.score - a.score || byName(a, b));
    }
    if (sort === 'recent') {
        const at = new Map(projects.map((p) => [p.id, Date.parse(p.updated_at ?? '') || 0]));
        return options.sort((a, b) => (at.get(b.id) ?? 0) - (at.get(a.id) ?? 0) || byName(a, b));
    }
    return options.sort(byName);
}
/** The label a closed picker shows. */
export function projectOptionLabel(option) {
    if (!option)
        return '';
    return option.path ? `${option.path} › ${option.name}` : option.name;
}
//# sourceMappingURL=project-search.js.map